import * as React from 'react';
import { createContext } from 'react';
import { useHistory } from 'react-router-dom';
import { ProviderDocType } from '@quikplow/quikplow-types';

import { APP_ROUTES } from '@root/appRoutes';
import { useAuth } from '../Auth';

const defaultState = {
  id: undefined,
  customer: undefined,
  provider: undefined,
  snowDepth: undefined,
  additionalServices: {
    stake: undefined,
    citySidewalkShovel: undefined,
    northWalkwayShovel: undefined,
    eastWalkwayShovel: undefined,
    southWalkwayShovel: undefined,
    westWalkwayShovel: undefined,
  },
  vehicle: {
    make: undefined,
    model: undefined,
    year: undefined,
    color: undefined,
    plate: undefined,
  },
  address: undefined,
  cost: undefined,
  status: undefined,
  stripePaymentMethod: undefined,
  activityTimeLog: {
    requestedTime: undefined,
  },
};

type State = any;
type ActionTypes = 'UPDATE_ACTIVATION_REQUEST' | 'RESET_ACTIVATION_REQUEST'
type Action = {
  type: ActionTypes
  payload?: {
    [key: string]: unknown
  }
};

type Reducer = (state: State, action: Action) => State
const reducer: Reducer = (state, action) => {
  const { type, payload } = action;
  let copyOfState = { ...state };

  switch (type) {
    case 'UPDATE_ACTIVATION_REQUEST':
      if (payload) {
        copyOfState = {
          ...copyOfState,
          ...payload,
          additionalServices: {
            ...(copyOfState?.additionalServices || {}),
            ...(typeof payload?.additionalServices === 'object' ? payload.additionalServices : {}),
          },
          vehicle: {
            ...(copyOfState?.vehicle || {}),
            ...(typeof payload?.vehicle === 'object' ? payload.vehicle : {}),
          },
          equipment: {
            ...(copyOfState?.equipment || {}),
            ...(typeof payload?.equipment === 'object' ? payload.equipment : {}),
          },
        };
      }
      break;

    case 'RESET_ACTIVATION_REQUEST':
      copyOfState = defaultState;
      break;

    default:
      break;
  }

  return copyOfState;
};


const MAX_STEPS_WITHOUT_PLOW = 7;
const MAX_STEPS_WITH_PLOW = 10;

type ContextType = {
  activationRequestState: State
  dispatchActivationRequestState: ({ type, payload }: Action) => void
  currentStep: number
  setCurrentStep: (stepIndex: number) => void
  stepBack: () => void
  stepForward: () => void
  maxSteps: number
}
const Context = createContext<ContextType>({
  activationRequestState: {},
  dispatchActivationRequestState: () => null,
  currentStep: 0,
  setCurrentStep: () => null,
  stepBack: () => null,
  stepForward: () => null,
  maxSteps: MAX_STEPS_WITHOUT_PLOW,
});

const ActivationRequestProvider = (props: any) => {
  const { children } = props;
  const { user } = useAuth();
  const provider = user as ProviderDocType;
  const history = useHistory();
  const [activationRequestState, dispatchActivationRequestState] = React.useReducer(reducer, { ...user });
  const [currentStep, setCurrentStep] = React.useState(0);
  const [maxSteps, setMaxSteps] = React.useState(MAX_STEPS_WITHOUT_PLOW);

  const stepBack = () => {
    setCurrentStep(Math.max(0, currentStep - 1));
  };

  const stepForward = () => {
    setCurrentStep(Math.min(maxSteps, currentStep + 1));
  };

  React.useEffect(() => {
    if (currentStep > maxSteps) {
      history.push(APP_ROUTES.authenticated.providers.home);
    }
  }, [currentStep, maxSteps, history]);

  React.useEffect(() => {
    if (user === null) {
      dispatchActivationRequestState({
        type: 'RESET_ACTIVATION_REQUEST',
      });
      setCurrentStep(0);
    } else {
      dispatchActivationRequestState({
        type: 'UPDATE_ACTIVATION_REQUEST',
        payload: { ...user },
      });
    }
  }, [user]);

  React.useEffect(() => {
    if (provider?.equipment?.plow === true) {
      setMaxSteps(MAX_STEPS_WITH_PLOW);
    } else {
      setMaxSteps(MAX_STEPS_WITHOUT_PLOW);
    }
  }, [provider]);

  return (
    <Context.Provider
      value={{
        activationRequestState,
        dispatchActivationRequestState,

        maxSteps,
        currentStep,
        setCurrentStep,
        stepBack,
        stepForward,
      }}
    >
      {children}
    </Context.Provider>
  );
};

const useActivationRequest = (): ContextType => React.useContext(Context);

export {
  ActivationRequestProvider,
  useActivationRequest,
};
